import * as React from "react";
import { useParams } from "react-router-dom";

import { useNavigation } from "@/providers";
import { Page } from "@/shared/components";
import { LoadingScreen } from "@/shared/components/LoadingScreen";
import type { SlackLinkNonceDisplayRecord } from "@/shared/domain/slack";
import {
  useClaimSlackLink,
  useSlackLinkNonce,
} from "@/shared/domain/slack/hooks";

import styles from "./LinkSlack.module.css";
import { LinkSlackCard } from "./LinkSlackCard";
import { LinkSlackUnavailable } from "./LinkSlackUnavailable";

/**
 * Completes the Slack account link a Slack ephemeral message pointed the reader at.
 *
 * The Roboto side of a link is whoever is signed in, never anything the request names. Linking
 * settles identity alone; which org's data that identity can reach is decided per turn against
 * live membership, which is also why the route requires an org — a link held by an org-less
 * account can reach nothing.
 */
export function LinkSlack() {
  const { slackLinkNonce } = useParams();
  const { goto } = useNavigation();

  const nonce = slackLinkNonce ?? "";
  const pendingLink = useSlackLinkNonce(nonce);
  const claim = useClaimSlackLink();

  const onConfirm = React.useCallback(() => {
    claim.mutate(nonce, { onSuccess: () => goto.home() });
  }, [claim, goto, nonce]);

  const onLeave = React.useCallback(() => goto.home(), [goto]);

  return (
    <Page title={"Roboto - Link Slack"}>
      <div className={styles.page}>
        <LinkSlackContent
          pending={pendingLink.data}
          isLoading={pendingLink.isPending}
          onConfirm={onConfirm}
          isConfirming={claim.isPending}
          claimFailed={claim.isError}
          onLeave={onLeave}
        />
      </div>
    </Page>
  );
}

interface LinkSlackContentProps {
  pending: SlackLinkNonceDisplayRecord | undefined;
  isLoading: boolean;
  onConfirm: () => void;
  isConfirming: boolean;
  claimFailed: boolean;
  onLeave: () => void;
}

function LinkSlackContent({
  pending,
  isLoading,
  onConfirm,
  isConfirming,
  claimFailed,
  onLeave,
}: LinkSlackContentProps) {
  if (isLoading) {
    return <LoadingScreen />;
  }

  if (pending === undefined) {
    return <LinkSlackUnavailable onDone={onLeave} />;
  }

  return (
    <LinkSlackCard
      pending={pending}
      onConfirm={onConfirm}
      isConfirming={isConfirming}
      // A claim that fails has almost always been spent or expired between load and confirm,
      // and the reader's move is the same either way: get a fresh link.
      errorMessage={
        claimFailed
          ? "That link could not be used. Mention @Roboto in Slack again to get a new one."
          : undefined
      }
      onCancel={onLeave}
    />
  );
}
